import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { User, UserProfileUpdate } from "@relais/shared";
import { Button, Drawer } from "@relais/design-system";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  Download,
  HardDrive,
  Lock,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Receipt,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import {
  buildAuditExport,
  getDb,
  getStorageBreakdown,
  getStorageEstimate,
  gzipJson,
  signalLevel,
  wipeDatabase,
  type SignalLevel,
  type StorageBreakdown,
  type StorageEstimateResult,
} from "@relais/offline";
import {
  setCachedProfile,
  signOut,
  useSession,
  useUserProfile,
} from "../lib/auth.js";
import { useActivityStats } from "../lib/activity.js";
import { ActivityIcon, HelpCenterIcon } from "../components/shortcut-icons.js";
import { PROFIL_CARD } from "../lib/profil-theme.js";
import { useOffline } from "../lib/offline-context.js";
import { apiFetch } from "../lib/api.js";
import { supabase } from "../lib/supabase.js";
import { departmentsByRegion } from "../lib/departments.js";
import {
  changePassword,
  validatePasswordChange,
  type PasswordValidationError,
} from "@relais/shared";

export const Route = createFileRoute("/profil")({
  component: ProfilePage,
});

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

function avatarPublicUrl(path: string | null): string | null {
  if (!path) return null;
  return supabase.storage.from("avatars").getPublicUrl(path).data.publicUrl;
}

function fullName(profile: User): string {
  const name = [profile.first_name, profile.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || profile.email;
}

function initials(profile: User): string {
  const chars = [profile.first_name?.[0], profile.last_name?.[0]]
    .filter(Boolean)
    .join("");
  return chars || profile.email[0]?.toUpperCase() || "?";
}

function ProfilePage() {
  const session = useSession();
  const { connectionState } = useOffline();

  if (session.status === "loading")
    return <p className="text-sm text-muted-foreground">…</p>;
  if (session.status === "anon") return <Navigate to="/login" />;

  return (
    <div className="flex flex-1 flex-col gap-6 pb-2">
      <ProfileSection online={connectionState === "online"} />
      <StorageSection />
      <LogoutButton />
    </div>
  );
}

function LogoutButton() {
  const navigate = useNavigate();
  return (
    <Button
      variant="destructive"
      className="mt-auto h-12 w-full text-base font-medium transition-transform duration-100 ease-out active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100"
      onClick={async () => {
        await signOut();
        navigate({ to: "/login" });
      }}
    >
      Déconnexion
    </Button>
  );
}

const SIGNAL_STYLE: Record<
  SignalLevel | "unknown",
  { dot: string; text: string; label: string; Icon: LucideIcon }
> = {
  green: {
    dot: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
    label: "Tout va bien",
    Icon: CheckCircle2,
  },
  orange: {
    dot: "bg-amber-500",
    text: "text-amber-600 dark:text-amber-400",
    label: "À surveiller",
    Icon: AlertTriangle,
  },
  red: {
    dot: "bg-red-500",
    text: "text-red-600 dark:text-red-500",
    label: "Stockage critique",
    Icon: AlertTriangle,
  },
  unknown: {
    dot: "bg-muted-foreground/40",
    text: "text-muted-foreground",
    label: "Mesure indisponible",
    Icon: HardDrive,
  },
};

function formatMo(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1);
}

function StorageSection() {
  const { pushToast } = useOffline();
  const [estimate, setEstimate] = useState<StorageEstimateResult | null>(null);
  const [breakdown, setBreakdown] = useState<StorageBreakdown | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const db = getDb();
      const [est, brk] = await Promise.all([
        getStorageEstimate(),
        getStorageBreakdown(db),
      ]);
      if (!alive) return;
      setEstimate(est);
      setBreakdown(brk);
      setLoaded(true);
    })().catch(() => {
      if (alive) setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const level: SignalLevel | "unknown" = estimate
    ? signalLevel(estimate.ratio)
    : "unknown";
  const style = SIGNAL_STYLE[level];

  async function onExport() {
    setExporting(true);
    try {
      const now = new Date();
      const data = await buildAuditExport(getDb(), now.toISOString());
      const { blob, extension } = await gzipJson(data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `relais-stockage-${now.toISOString().slice(0, 10)}.${extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      pushToast({ kind: "success", message: "Export téléchargé." });
    } catch {
      pushToast({ kind: "error", message: "Échec de l'export. Réessayez." });
    } finally {
      setExporting(false);
    }
  }

  const mediaCount = breakdown?.counts.medias ?? 0;
  const pending = breakdown?.pendingMutations ?? 0;

  return (
    <section className={"space-y-3 rounded-xl p-4 " + PROFIL_CARD}>
      <div className="min-w-0">
        <h2 className="text-base font-medium text-foreground">
          Stockage de l’app
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Données enregistrées sur cet appareil pour le mode hors-ligne.
        </p>
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5">
        <span
          className={"size-2.5 shrink-0 rounded-full " + style.dot}
          aria-hidden
        />
        <style.Icon className={"size-4 shrink-0 " + style.text} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className={"truncate text-sm font-medium " + style.text}>
            {style.label}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {!loaded
              ? "Mesure en cours…"
              : estimate
                ? `${formatMo(estimate.usage)} Mo / ${formatMo(estimate.quota)} Mo (${Math.round(estimate.ratio * 100)} %)`
                : "Espace non mesurable sur cet appareil."}
            {loaded && breakdown
              ? ` · ${mediaCount} photo${mediaCount > 1 ? "s" : ""} · ${pending} en attente`
              : ""}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          variant="outline"
          className="flex-1 gap-2"
          onClick={onExport}
          disabled={exporting || !loaded}
        >
          <Download className="size-4" aria-hidden />
          {exporting ? "Export…" : "Exporter les données"}
        </Button>
        <Button
          variant="destructive"
          className="flex-1 gap-2"
          onClick={() => setConfirmOpen(true)}
          disabled={!loaded}
        >
          <Trash2 className="size-4" aria-hidden />
          Vider les données
        </Button>
      </div>

      <WipeConfirmDrawer
        open={confirmOpen}
        pending={pending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await wipeDatabase(getDb());
          window.location.reload();
        }}
      />
    </section>
  );
}

function WipeConfirmDrawer({
  open,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  pending: number;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open) {
      setTyped("");
      setBusy(false);
    }
  }, [open]);

  const needsPhrase = pending > 0;
  const canConfirm = !needsPhrase || typed.trim().toUpperCase() === "SUPPRIMER";

  async function confirm() {
    if (!canConfirm) return;
    setBusy(true);
    try {
      // Succès → la page se recharge (pas de setBusy(false) nécessaire).
      await onConfirm();
    } catch {
      setBusy(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Vider les données"
      description="Supprime toutes les données hors-ligne de cet appareil."
    >
      <div className="flex h-full flex-col gap-4">
        {needsPhrase ? (
          <div className="space-y-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
            <p className="flex items-center gap-2 text-sm font-medium text-destructive">
              <AlertTriangle className="size-4 shrink-0" aria-hidden />
              {pending} modification{pending > 1 ? "s" : ""} pas encore envoyée
              {pending > 1 ? "s" : ""}
            </p>
            <p className="text-sm text-muted-foreground">
              Ces modifications seront définitivement perdues. Exportez vos
              données ou reconnectez-vous pour synchroniser avant de vider.
            </p>
            <label className="block pt-1">
              <span className="text-xs text-muted-foreground">
                Tapez <strong>SUPPRIMER</strong> pour confirmer
              </span>
              <input
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground outline-none focus-visible:border-destructive focus-visible:ring-2 focus-visible:ring-destructive/40"
              />
            </label>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Toutes les données hors-ligne (tâches, photos, brouillons) seront
            supprimées de cet appareil. Cette action est irréversible.
          </p>
        )}
        <div className="mt-auto flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={busy}
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            onClick={confirm}
            disabled={busy || !canConfirm}
          >
            {busy ? "Suppression…" : "Tout supprimer"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

function ProfileSection({ online }: { online: boolean }) {
  const profile = useUserProfile();
  // Profil en cache → on affiche la carte même hors connexion (édition
  // désactivée). Sans cache et hors ligne, rien à montrer.
  if (profile.status === "ready")
    return <ProfileCard initial={profile.profile} online={online} />;
  if (!online)
    return (
      <p className="text-sm text-muted-foreground">
        Vos informations de profil ne sont pas disponibles hors connexion.
      </p>
    );
  if (profile.status === "error")
    return (
      <p className="text-sm text-destructive">
        Impossible de charger votre profil.
      </p>
    );
  return <p className="text-sm text-muted-foreground">Chargement…</p>;
}

type EditableField = "phone" | "department";

function ProfileCard({ initial, online }: { initial: User; online: boolean }) {
  const { pushToast } = useOffline();
  const stats = useActivityStats();
  const [profile, setProfile] = useState(initial);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editing, setEditing] = useState<EditableField | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function applyUpdated(updated: User) {
    setCachedProfile(updated);
    setProfile(updated);
  }

  async function saveField(field: EditableField, raw: string) {
    const value = raw.trim();
    if (field === "phone" && value && value.length < 3) {
      throw new Error("Numéro de téléphone trop court.");
    }
    const payload: UserProfileUpdate = { [field]: value || null };
    const updated = await apiFetch<User>("/users/me", {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    applyUpdated(updated);
    pushToast({ kind: "success", message: "Profil enregistré." });
  }

  async function onPhotoPicked(file: File) {
    if (!file.type.startsWith("image/")) {
      pushToast({ kind: "error", message: "Le fichier doit être une image." });
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      pushToast({ kind: "error", message: "Image trop lourde (5 Mo max)." });
      return;
    }
    setUploadingPhoto(true);
    const previousPath = profile.avatar_path;
    const ext =
      (file.name.split(".").pop() ?? "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .slice(0, 8) || "jpg";
    const path = `${profile.id}/avatar-${Date.now()}.${ext}`;
    try {
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(path, file, { contentType: file.type, upsert: true });
      if (uploadError) throw uploadError;
      const updated = await apiFetch<User>("/users/me", {
        method: "PATCH",
        body: JSON.stringify({ avatar_path: path } satisfies UserProfileUpdate),
      });
      applyUpdated(updated);
      if (previousPath && previousPath !== path) {
        // Nettoyage best-effort de l'ancienne photo.
        supabase.storage
          .from("avatars")
          .remove([previousPath])
          .catch(() => {});
      }
      pushToast({ kind: "success", message: "Photo de profil mise à jour." });
    } catch {
      pushToast({ kind: "error", message: "Échec de l'envoi de la photo. Réessayez." });
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const avatarUrl = avatarPublicUrl(profile.avatar_path);

  return (
    <>
      {/* En-tête : nom + photo */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold text-foreground">
            {fullName(profile)}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {stats.status === "ready" && (
              <span className={"inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs " + PROFIL_CARD}>
                <strong className="font-semibold text-primary tabular-nums">
                  {stats.stats.total}
                </strong>
                <span className="text-muted-foreground">
                  {stats.stats.total > 1 ? "Missions" : "Mission"}
                </span>
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          disabled={!online || uploadingPhoto}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Changer la photo de profil"
          aria-busy={uploadingPhoto}
          className="group relative size-16 shrink-0 rounded-full outline-none ring-2 ring-primary ring-offset-2 ring-offset-background transition-transform duration-100 ease-out focus-visible:ring-4 active:scale-95 disabled:cursor-not-allowed disabled:active:scale-100 motion-reduce:transition-none motion-reduce:active:scale-100"
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="size-full rounded-full object-cover"
            />
          ) : (
            <span
              aria-hidden
              className="flex size-full items-center justify-center rounded-full bg-muted text-lg font-semibold text-muted-foreground"
            >
              {initials(profile)}
            </span>
          )}
          {online && (
            <>
              {/* Voile + icône au survol (desktop) */}
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/45 opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none">
                <Camera className="size-5 text-white" aria-hidden />
              </span>
              {/* Pastille caméra (toujours visible → découvrable au tactile) */}
              <span className="absolute -bottom-0.5 -right-0.5 flex size-6 items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground">
                <Camera className="size-3" aria-hidden />
              </span>
            </>
          )}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          aria-label="Photo de profil"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onPhotoPicked(file);
          }}
        />
      </div>

      {/* Raccourcis (visuels) */}
      <div className="grid grid-cols-2 gap-3">
        <ShortcutCard to="/profil/aide" icon={<HelpCenterIcon className="size-7" />} label="Centre d’aide" />
        <ShortcutCard to="/profil/activite" icon={<ActivityIcon className="size-7" />} label="Votre activité" />
        <ShortcutCard to="/profil/factures" icon={<Receipt className="size-7" />} label="Vos factures" />
      </div>

      {/* Vos informations */}
      <section className={"space-y-3 rounded-xl p-4 " + PROFIL_CARD}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-medium text-foreground">Vos informations</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Mettez à jour vos informations !
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing("phone")}
            disabled={!online}
            aria-label="Modifier vos informations"
            className="flex size-9 shrink-0 items-center justify-center rounded-full text-foreground outline-none transition-colors duration-150 ease hover:bg-background focus-visible:bg-background disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Pencil className="size-4" aria-hidden />
          </button>
        </div>

        <div className="space-y-2.5">
          <InfoRow
            icon={<Phone className="size-4" aria-hidden />}
            value={profile.phone}
            placeholder="Ajouter un téléphone"
            onClick={() => setEditing("phone")}
            disabled={!online}
          />
          <InfoRow
            icon={<Mail className="size-4" aria-hidden />}
            value={profile.email}
            locked
            lockedHint="L’email sert d’identifiant et ne peut pas être modifié ici."
          />
          <InfoRow
            icon={<MapPin className="size-4" aria-hidden />}
            value={profile.department}
            placeholder="Ajouter un département"
            onClick={() => setEditing("department")}
            disabled={!online}
          />
        </div>
      </section>

      {/* Sécurité */}
      <section className={"space-y-3 rounded-xl p-4 " + PROFIL_CARD}>
        <div className="min-w-0">
          <h2 className="text-base font-medium text-foreground">Sécurité</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Gérez l’accès à votre compte.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setChangingPassword(true)}
          disabled={!online}
          className="flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 text-left outline-none transition-[transform,border-color] duration-100 ease-out hover:border-primary/50 focus-visible:border-primary active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 motion-reduce:transition-none motion-reduce:active:scale-100"
        >
          <span className="text-muted-foreground">
            <Lock className="size-4" aria-hidden />
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
            Modifier le mot de passe
          </span>
          <Pencil className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </section>

      <EditFieldDrawer
        field={editing}
        profile={profile}
        onClose={() => setEditing(null)}
        onSave={saveField}
      />

      <PasswordDrawer
        open={changingPassword}
        email={profile.email}
        online={online}
        onClose={() => setChangingPassword(false)}
        onSuccess={() =>
          pushToast({ kind: "success", message: "Mot de passe mis à jour." })
        }
      />
    </>
  );
}

function ShortcutCard({
  icon,
  label,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className={
        "flex select-none flex-col items-center justify-center gap-2 rounded-xl px-4 py-4 text-foreground outline-none transition-[transform,filter] duration-100 ease-out hover:brightness-[0.97] focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100 " +
        PROFIL_CARD
      }
    >
      {icon}
      <span className="text-base font-medium">{label}</span>
    </Link>
  );
}

function InfoRow({
  icon,
  value,
  placeholder,
  onClick,
  locked,
  lockedHint,
  disabled,
}: {
  icon: React.ReactNode;
  value: string | null;
  placeholder?: string;
  onClick?: () => void;
  locked?: boolean;
  lockedHint?: string;
  disabled?: boolean;
}) {
  const hasValue = Boolean(value && value.trim());
  const content = (
    <>
      <span className="text-muted-foreground">{icon}</span>
      <span
        className={
          "min-w-0 flex-1 truncate text-sm font-medium " +
          (hasValue ? "text-foreground" : "text-muted-foreground")
        }
      >
        {hasValue ? value : placeholder}
      </span>
    </>
  );

  // Verrouillé (email) ou édition désactivée (hors connexion) → non cliquable.
  if (locked || disabled) {
    return (
      <div
        title={lockedHint}
        aria-disabled={disabled || undefined}
        className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5"
      >
        {content}
        {locked && (
          <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 text-left outline-none transition-[transform,border-color] duration-100 ease-out hover:border-primary/50 focus-visible:border-primary active:scale-[0.99] motion-reduce:transition-none motion-reduce:active:scale-100"
    >
      {content}
      <Pencil className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  );
}

const FIELD_META: Record<
  EditableField,
  { label: string; type: string; placeholder: string }
> = {
  phone: { label: "Téléphone", type: "tel", placeholder: "+33 6 12 34 56 78" },
  department: {
    label: "Département",
    type: "text",
    placeholder: "Ex. 34 — Hérault, Occitanie",
  },
};

function EditFieldDrawer({
  field,
  profile,
  onClose,
  onSave,
}: {
  field: EditableField | null;
  profile: User;
  onClose: () => void;
  onSave: (field: EditableField, value: string) => Promise<void>;
}) {
  const open = field !== null;
  const meta = field ? FIELD_META[field] : null;
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Réinitialise le champ à chaque ouverture sur la valeur courante.
  useEffect(() => {
    if (!field) return;
    setValue(profile[field] ?? "");
    setError(null);
    setSaving(false);
  }, [field, profile]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!field) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(field, value);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Enregistrement impossible. Vérifiez votre connexion.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={saving ? () => {} : onClose}
      title={meta ? meta.label : ""}
      description="Mettez à jour cette information."
    >
      <form onSubmit={submit} className="flex h-full flex-col gap-4">
        <label className="block">
          <span className="text-sm text-muted-foreground">{meta?.label}</span>
          {field === "department" ? (
            <select
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              <option value="">Sélectionner un département…</option>
              {departmentsByRegion().map(({ region, items }) => (
                <optgroup key={region} label={region}>
                  {items.map((d) => {
                    const v = `${d.name}, ${d.region}`;
                    return (
                      <option key={d.code} value={v}>
                        {d.code} — {d.name}
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
          ) : (
            <input
              autoFocus
              type={meta?.type}
              value={value}
              placeholder={meta?.placeholder}
              onChange={(e) => setValue(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/40"
            />
          )}
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="mt-auto flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button type="submit" className="flex-1" disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

const PASSWORD_VALIDATION_MESSAGES: Record<PasswordValidationError, string> = {
  empty: "Renseignez un nouveau mot de passe.",
  same: "Le nouveau mot de passe doit être différent de l’actuel.",
  mismatch: "Les mots de passe ne correspondent pas.",
};

function PasswordDrawer({
  open,
  email,
  online,
  onClose,
  onSuccess,
}: {
  open: boolean;
  email: string;
  online: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vide les champs à chaque changement d'état ouverture/fermeture : aucun mot
  // de passe ne subsiste en mémoire après fermeture.
  useEffect(() => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError(null);
    setSaving(false);
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const invalid = validatePasswordChange({ current, next, confirm });
    if (invalid) {
      setError(PASSWORD_VALIDATION_MESSAGES[invalid]);
      return;
    }
    setSaving(true);
    setError(null);
    // changePassword ne rejette jamais → setSaving(false) s'exécute toujours.
    const res = await changePassword(supabase, { email, current, next });
    setSaving(false);
    if (!res.ok) {
      // update_failed : on montre le message Supabase (ex. « trop court »),
      // conformément à la spec côté relais.
      setError(
        res.code === "wrong_current"
          ? "Mot de passe actuel incorrect."
          : res.code === "update_failed"
            ? res.message
            : "Connexion interrompue. Vérifiez votre réseau et réessayez.",
      );
      return;
    }
    onSuccess();
    onClose();
  }

  const inputClass =
    "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/40";

  return (
    <Drawer
      open={open}
      onClose={saving ? () => {} : onClose}
      title="Changer le mot de passe"
      description="Renseignez votre mot de passe actuel puis le nouveau."
    >
      {!online ? (
        // Le changement exige le réseau (2 appels Supabase Auth). Le déclencheur
        // est déjà désactivé hors ligne ; ce garde couvre le passage online→offline
        // drawer ouvert.
        <p className="text-sm text-muted-foreground">
          Connexion requise pour changer le mot de passe.
        </p>
      ) : (
      <form onSubmit={submit} className="flex h-full flex-col gap-4">
        <label className="block">
          <span className="text-sm text-muted-foreground">Mot de passe actuel</span>
          <input
            autoFocus
            required
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className="text-sm text-muted-foreground">Nouveau mot de passe</span>
          <input
            required
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={inputClass}
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            6 caractères minimum.
          </span>
        </label>
        <label className="block">
          <span className="text-sm text-muted-foreground">
            Confirmer le nouveau mot de passe
          </span>
          <input
            required
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputClass}
          />
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="mt-auto flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button type="submit" className="flex-1" disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </form>
      )}
    </Drawer>
  );
}
